import { Component, HostListener } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { WebRecordedStep } from '../../Models/web-recorded-command-model';
import { RecordedStepsService } from '../../Services/recorded-steps.service';
import { RecorderSessionService } from '../../Services/recorder-session.service';
import { NamePromptService } from '../../Services/name-prompt.service';
import { RestService } from '../../Services/rest.service';
import { StepSaveService } from '../../Services/step-save.service';
import { ConfirmDialogModel, DialogComponent } from '../../AppBaseComponents/dialog/dialog.component';

// The single full-width toolbar above the sidebar + simulator — merges what
// used to be two separate bars (the sidebar's per-step action toolbar and
// this component's own step/record controls) into one, per the updated
// design: selecting/creating a step is both the view target AND the
// recording target, so there's no separate "recording into" dropdown.
@Component({
  selector: 'ze-simulator-toolbar',
  templateUrl: './simulator-toolbar.component.html',
  styleUrls: ['./simulator-toolbar.component.scss']
})
export class SimulatorToolbarComponent {

  saveState: 'idle' | 'saving' | 'success' | 'error' = 'idle';
  toastMessage: string | null = null;
  toastType: 'success' | 'error' = 'success';
  private toastTimer: any = null;

  // "Remove Duplicate Clicks" dedupes consecutive identical commands. "Save
  // Steps" (stepSave.saveStepsChecked) persists pending commands to the
  // backend on session close. "Parameterize Steps" is an inert UI toggle.
  optionsMenuOpen = false;
  removeDuplicatesApplied = false;
  parameterizeStepsChecked = false;

  toggleOptionsMenu(event: MouseEvent) {
    event.stopPropagation();
    this.optionsMenuOpen = !this.optionsMenuOpen;
  }

  @HostListener('document:click')
  closeOptionsMenu() {
    this.optionsMenuOpen = false;
  }

  onToggleRemoveDuplicates() {
    this.removeDuplicatesApplied = !this.removeDuplicatesApplied;
    if (this.removeDuplicatesApplied) {
      this.steps.steps.forEach(step => this.steps.removeDuplicateClicks(step));
    }
  }

  constructor(
    public steps: RecordedStepsService,
    public recorderSession: RecorderSessionService,
    private namePrompt: NamePromptService,
    private restService: RestService,
    public stepSave: StepSaveService,
    private dialog: MatDialog
  ) {}

  // Recording keeps appending to recordingStep regardless of what's
  // selected (see RecordedStepsService) — going back while still recording
  // used to be silently allowed, which left the recorder running against a
  // step the user could no longer see. Block it and make them explicitly
  // stop first, so there's no "wait, is it still recording?" confusion.
  back() {
    if (this.recorderSession.isRecording) {
      const dialogData = new ConfirmDialogModel(
        'Recording in Progress',
        'Please stop the recording before going back to the steps list. Stop recording now and go back?'
      );
      this.dialog.open(DialogComponent, { data: dialogData }).afterClosed().subscribe(confirmed => {
        if (confirmed) this.stopRecording(() => this.steps.selectStep(null));
      });
      return;
    }
    this.steps.selectStep(null);
  }

  async addStep() {
    const name = await this.namePrompt.open('New Step', 'New Step');
    if (!name) return;
    const step = this.steps.newStep(name);
    // Creating a step also opens it — it's immediately both the view target
    // and the recording target.
    this.steps.selectStep(step);
  }

  // Starting a recording is a real round trip — REST call, then ataserver
  // opens a CDP websocket, enables Page/Runtime domains, and injects/
  // evaluates the full ~40k-line bundle — genuinely takes a moment with
  // nothing to show for it otherwise. Same for Stop (it evaluates a
  // detach() call before closing the socket).
  recordingPending = false;

  toggleRecording() {
    if (!this.recorderSession.sessionId || this.recordingPending) return;
    // Starting a new recording needs a step open to record into — but an
    // already-running recording must still be stoppable even after
    // navigating back to the list (recordingStep persists independently of
    // selectedStep, see RecordedStepsService).
    if (!this.recorderSession.isRecording && !this.steps.selectedStep) return;
    if (this.recorderSession.isRecording) {
      this.stopRecording();
    } else {
      this.startRecordingCall();
    }
  }

  private startRecordingCall() {
    const projectId = localStorage.getItem('projectIdDb2') || '';
    this.recordingPending = true;
    this.restService.post('startRecorderRecording', { projectId }).subscribe(
      (res: any) => {
        this.recordingPending = false;
        // These endpoints return HTTP 200 even on failure (the error is in
        // the response body), so a resolved subscribe callback does NOT by
        // itself mean recording actually started on the server.
        if (res && res.operationStatus === 'ERROR') {
          this.showToast('error', res.operationMessage || 'Failed to start recording.');
          return;
        }
        this.recorderSession.isRecording = true;
      },
      (err) => {
        this.recordingPending = false;
        this.showToast('error', 'Failed to start recording.');
      }
    );
  }

  // Shared by the Record/Stop button and by back()'s "stop and go back"
  // confirmation — onStopped only runs once the server confirms the stop.
  // "Save Steps" no longer auto-saves here; it runs on session close instead.
  private stopRecording(onStopped?: () => void) {
    const projectId = localStorage.getItem('projectIdDb2') || '';
    this.recordingPending = true;
    this.restService.post('stopRecorderRecording', { projectId }).subscribe(
      (res: any) => {
        this.recordingPending = false;
        if (res && res.operationStatus === 'ERROR') {
          this.showToast('error', res.operationMessage || 'Failed to stop recording.');
          return;
        }
        this.recorderSession.isRecording = false;
        if (onStopped) onStopped();
      },
      (err) => {
        this.recordingPending = false;
        this.showToast('error', 'Failed to stop recording.');
      }
    );
  }

  async onRenameStep() {
    const step = this.steps.selectedStep;
    if (!step) return;
    const name = await this.namePrompt.open('Rename Step', step.name);
    if (name) this.steps.renameStep(step, name);
  }

  onDeleteStep() {
    const step = this.steps.selectedStep;
    if (!step) return;
    this.steps.deleteStep(step);
  }

  onCopyStep() {
    const step = this.steps.selectedStep;
    if (!step) return;
    this.steps.copyStep(step);
  }

  onSaveToCanvas() {
    const step = this.steps.selectedStep;
    if (!step) return;
    const stepName = (step.name || '').trim();
    if (!stepName) {
      this.showToast('error', 'Cannot save an unnamed step. Please give the step a name first.');
      return;
    }
    if (!step.commands || step.commands.length === 0) {
      this.showToast('error', 'Cannot save an empty step. Record at least one command before saving.');
      return;
    }
    const projectId = localStorage.getItem('projectIdDb2');
    if (!projectId) {
      this.showToast('error', 'No project selected. Please open a project first.');
      return;
    }

    this.saveState = 'saving';

    // Already saved once — appending to its own name, so no duplicate check needed.
    if (step.savedStepId) {
      this.stepSave.saveStep(step, projectId).subscribe(res => this.handleSaveResult(step, stepName, res));
      return;
    }
    this.stepSave.checkNameAvailable(projectId, stepName).subscribe(available => {
      if (!available) {
        this.saveState = 'idle';
        this.showToast('error', `A step named "${stepName}" already exists. Please rename this step first.`);
        return;
      }
      this.stepSave.saveStep(step, projectId).subscribe(res => this.handleSaveResult(step, stepName, res));
    });
  }

  private handleSaveResult(step: WebRecordedStep, stepName: string, res: any): void {
    this.saveState = 'idle';
    if (!res) {
      this.showToast('error', 'No new commands to save since the last save.');
      return;
    }
    // TempObject.add() skips commands with no matching action and reports them here.
    const unresolved: string[] = res && res.data && res.data.UnresolvedCommands;
    if (unresolved && unresolved.length > 0) {
      this.showToast('error', `"${stepName}" saved, but ${unresolved.length} command(s) could not be `
        + `saved (no matching action: ${unresolved.join(', ')})`);
    } else {
      this.showToast('success', `"${stepName}" saved to Canvas successfully`);
    }
  }

  private showToast(type: 'success' | 'error', message: string): void {
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastType = type;
    this.toastMessage = message;
    this.toastTimer = setTimeout(() => { this.toastMessage = null; this.toastTimer = null; }, 3500);
  }
}
